import aboutData from "./about.json"
import homepageData from "./homepage.json"
import type { PreviewDraft } from "./preview"
import { stripPodcastPlayers } from "./sanitize"
import { site as publishedSite } from "./site"
import storiesData from "./stories.json"
import type { AboutPageConfig, HomePageConfig, SiteConfig, WorkPageConfig } from "./types"
import workData from "./work.json"

export const bundledContent: PreviewDraft = {
  site: publishedSite,
  homepage: homepageData as HomePageConfig,
  stories: sanitizeStories(storiesData as PreviewDraft["stories"]),
  work: workData as WorkPageConfig,
  about: aboutData as AboutPageConfig,
}

function sanitizeStories(stories: PreviewDraft["stories"]): PreviewDraft["stories"] {
  return {
    posts: stories.posts.map((post) => ({
      ...post,
      excerpt: stripPodcastPlayers(post.excerpt || ""),
      body: stripPodcastPlayers(post.body || ""),
    })),
  }
}

async function fetchJson<T>(url: string): Promise<T | undefined> {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
    if (!response.ok) return undefined
    return (await response.json()) as T
  } catch {
    return undefined
  }
}

async function loadFile<T>(filename: string, fallback: T): Promise<T> {
  const bust = `v=${Date.now()}`
  return (await fetchJson<T>(`/content/${filename}?${bust}`)) ?? fallback
}

/** Load published content from the same Name.com host (`/content/*.json`). */
export async function loadLiveContent(): Promise<PreviewDraft> {
  const [site, homepage, stories, work, about] = await Promise.all([
    loadFile<SiteConfig>("site.json", bundledContent.site),
    loadFile<HomePageConfig>("homepage.json", bundledContent.homepage),
    loadFile<PreviewDraft["stories"]>("stories.json", bundledContent.stories),
    loadFile<WorkPageConfig>("work.json", bundledContent.work),
    loadFile<AboutPageConfig>("about.json", bundledContent.about),
  ])

  return {
    site,
    homepage,
    stories: sanitizeStories(stories),
    work,
    about,
  }
}
